import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { officeExpenseAPI } from '../../services/api';
import ErrorModal from '../Common/ErrorModal';
import { saveAs } from 'file-saver';
import './OfficeExpenseCreate.css';

const OfficeExpenseView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadExpense();
  }, [id]);

  const loadExpense = async () => {
    try {
      setLoading(true);
      const response = await officeExpenseAPI.getById(id);
      const data = response.data;
      const dateStr = new Date(data.date).toISOString().split('T')[0];
      setExpense({
        ...data,
        date: dateStr,
        amount: data.amount || '',
        quantity: data.quantity || '',
      });
    } catch (err) {
      setErrorMessage(t('officeExpense.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      const response = await officeExpenseAPI.printSingle(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Print error:', error);
      setErrorMessage('Failed to generate PDF. Please try again.');
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('officeExpense.list.messages.noData')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header-actions">
          <button className="btn-back" onClick={() => navigate('/office-expense')}>
            <i className="fas fa-arrow-left"></i> {t('officeExpense.view.backToList')}
          </button>
          <button className="btn-edit" onClick={() => navigate(`/office-expense/edit/${id}`)}>
            <i className="fas fa-edit"></i> {t('officeExpense.view.edit')}
          </button>
          <button className="btn-primary" onClick={handlePrint} style={{ marginLeft: '10px' }}>
            <i className="fas fa-print"></i> {t('common.print', 'Print')}
          </button>
        </div>

      <div className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-calendar"></i> {t('officeExpense.view.fields.date')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-calendar input-icon"></i>
              <input
                type="date"
                className="form-input"
                value={expense.date}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-dollar-sign"></i> {t('officeExpense.view.fields.amount')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-dollar-sign input-icon"></i>
              <input
                type="number"
                step="0.01"
                className="form-input"
                value={expense.amount}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-list"></i> {t('officeExpense.view.fields.quantity')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-list input-icon"></i>
              <input
                type="number"
                className="form-input"
                value={expense.quantity || ''}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-align-left"></i> {t('officeExpense.view.fields.description')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-align-left input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={expense.description || ''}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-file-invoice"></i> {t('officeExpense.view.fields.invoiceNumber')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-file-invoice input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={expense.invoiceNumber || ''}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-user"></i> {t('officeExpense.view.fields.invoiceOwner')}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user input-icon"></i>
              <input
                type="text"
                className="form-input"
                value={expense.invoiceOwner || ''}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default OfficeExpenseView;


